import type { StoredEvent } from "@facade/dashboard-shared";
import { useEffect, useState } from "react";
import { blobRef, fmtMs, fmtTime } from "../lib/format";
import { api } from "../lib/api";
import { JsonView } from "./JsonView";

function ContentBlocks({ content }: { content: unknown }) {
  if (typeof content === "string") return <pre>{content}</pre>;
  if (!Array.isArray(content)) return <JsonView value={content} />;
  return (
    <>
      {content.map((b: any, i: number) => {
        const t = b?.type;
        if (t === "text") return <pre key={i} className="block">{b.text}</pre>;
        if (t === "image") {
          // The device replaces large base64 payloads with a blob ref ({blob, mime, bytes}) — same bytes, same sha.
          const ref = blobRef(b.source?.data);
          const src =
            b.source?.type === "base64"
              ? ref
                ? api.blobUrl(ref.sha)
                : `data:${b.source.media_type};base64,${b.source.data}`
              : b.source?.url;
          return (
            <div key={i} className="block">
              <span className="tag">image</span>
              {src ? <img src={src} style={{ maxWidth: 300, maxHeight: 400, display: "block", borderRadius: 6 }} alt="image block" /> : <JsonView value={b} />}
            </div>
          );
        }
        if (t === "tool_use")
          return (
            <div key={i} className="block tool">
              <span className="tag">tool_use · {b.name}</span>
              <JsonView value={b.input} />
            </div>
          );
        if (t === "tool_result")
          return (
            <div key={i} className="block tool">
              <span className="tag">tool_result · {b.tool_use_id}{b.is_error ? " · ERROR" : ""}</span>
              <ContentBlocks content={b.content} />
            </div>
          );
        if (t === "thinking") return <pre key={i} className="block muted">{b.thinking}</pre>;
        return <JsonView key={i} value={b} />;
      })}
    </>
  );
}

export function ModelQueryDrawer({ e, onClose, onPrev, onNext }: { e: StoredEvent; onClose: () => void; onPrev?: () => void; onNext?: () => void }) {
  const p = e.payload as any;
  const [tab, setTab] = useState<"conversation" | "raw">("conversation");
  useEffect(() => {
    const h = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
      if (ev.key === "ArrowLeft" && onPrev) onPrev();
      if (ev.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, onPrev, onNext]);
  const req = p?.request ?? {};
  const res = p?.response;
  const system = req.system;
  const messages: any[] = Array.isArray(req.messages) ? req.messages : [];
  return (
    <div className="drawer">
      <button className="close" onClick={onClose}>
        ✕
      </button>
      <h2 style={{ marginRight: 40 }}>
        model.query · <span className="mono">{p?.purpose}</span>
      </h2>
      <div className="row" style={{ fontSize: 13 }}>
        <span className={`badge ${p?.ok === false ? "fail" : "ok"}`}>{p?.ok === false ? "error" : "ok"}</span>
        <span className="mono">{p?.model}</span>
        <span className="muted">{p?.provider ?? p?.baseUrl}</span>
        <span className="muted">{fmtTime(e.ts)}</span>
        <span className="muted">{fmtMs(p?.latencyMs)}</span>
        {p?.usage && (
          <span className="muted">
            in {p.usage.input ?? 0} · out {p.usage.output ?? 0}
            {p.usage.cacheRead ? ` · cache-read ${p.usage.cacheRead}` : ""}
            {p.usage.cacheWrite ? ` · cache-write ${p.usage.cacheWrite}` : ""}
          </span>
        )}
        <span style={{ marginLeft: "auto" }} className="row">
          <button className="small" onClick={onPrev} disabled={!onPrev}>
            ← prev
          </button>
          <button className="small" onClick={onNext} disabled={!onNext}>
            next →
          </button>
        </span>
      </div>
      {p?.error && <div className="banner" style={{ marginTop: 8, color: "var(--bad)" }}>{p.error}</div>}
      <div className="tabs">
        <button className={tab === "conversation" ? "on" : ""} onClick={() => setTab("conversation")}>
          conversation
        </button>
        <button className={tab === "raw" ? "on" : ""} onClick={() => setTab("raw")}>
          raw JSON
        </button>
      </div>
      {tab === "raw" ? (
        <JsonView value={p} />
      ) : (
        <div>
          {system && (
            <div className="msg system">
              <div className="role">system</div>
              <ContentBlocks content={system} />
            </div>
          )}
          {req.tools && Array.isArray(req.tools) && req.tools.length > 0 && (
            <details className="msg system">
              <summary className="role" style={{ cursor: "pointer" }}>
                tools ({req.tools.length}): {req.tools.map((t: any) => t.name).join(", ")}
              </summary>
              <JsonView value={req.tools} />
            </details>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`msg ${m.role}`}>
              <div className="role">{m.role}</div>
              <ContentBlocks content={m.content} />
            </div>
          ))}
          <div className="msg assistant" style={{ borderStyle: "dashed" }}>
            <div className="role">response{res?.stop_reason ? ` · ${res.stop_reason}` : ""}</div>
            {res ? <ContentBlocks content={res.content ?? res} /> : <span className="muted">no response</span>}
          </div>
          {res?.usage && (
            <details style={{ marginTop: 8 }}>
              <summary className="muted">usage</summary>
              <JsonView value={res.usage} />
            </details>
          )}
        </div>
      )}
    </div>
  );
}
